import { ZONA_DE_OPERACION } from '@/shared/lib/dateUtils'
import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Revelar } from "@/shared/components/Revelar";
import { EsqueletoTabla, EsqueletoTarjetas } from "@/shared/components/Esqueleto";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Wrench,
  ArrowLeft,
  Download,
} from "lucide-react";
import { apiService } from '@/services/core/api.service';
import { herramientasApi } from '@/features/almacen/api/herramientas.api';
import PhotoViewerModal from '@/features/almacen/components/PhotoViewerModal';
import FilterSelect from '@/shared/components/FilterSelect';
import { useEstadoEnUrl } from '@/shared/hooks/useEstadoEnUrl';
import { GrupoDeEvidencia } from "@/features/almacen/components/GrupoDeEvidencia";
import { SelloDeAceptacion } from "@/features/almacen/components/SelloDeAceptacion";
import {
  RETORNO_POR_RECHAZO,
  accionDelMovimiento,
} from "@/features/almacen/lib/retornoPorRechazo";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";

// ─── Types ────────────────────────────────────────────────────────────────────

type ActividadTipo = "Reciente" | string;
type SortDir = "asc" | "desc" | null;

interface HerramientaHistorial {
  id: string;
  nombre: string;
  codigo: string;
  internalCode: string;
  fecha: string;
  createdAt: string;
  actividad: ActividadTipo;
  photoUrl: string | null;
  /** Para marcar las dadas de baja, que también se consultan aquí (A-09). */
  dadaDeBaja?: boolean;
}

type AccionTipo =
  | "Asignación"
  | "Reasignación"
  | "Mantenimiento"
  | "Devolución"
  | "Incidencia"
  | "Traspaso"
  | "Reactivación"
  | "Baja"
  // El retorno que provoca un rechazo (A-08). El back lo guarda como
  // reasignación o devolución según a dónde vuelva la herramienta, así que sin
  // etiqueta propia se leía como un movimiento cualquiera.
  | typeof RETORNO_POR_RECHAZO
  // Para un tipo de evento que el back mande y aquí todavía no esté mapeado.
  // Antes el fallback era "Asignación", que no avisa de nada: cuando el back
  // sumó las incidencias a la cronología, se habrían visto como asignaciones.
  | "Otro";

interface HistorialEntry {
  id: string;
  accion: AccionTipo;
  fechaHora: string;
  responsable: string;
  realizadoPor: string;
  proyectoAsignado: string;
  notes?: string | null;
  decommissionReason?: string | null;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
  // Las de quien recibe, al aceptar o rechazar (A-01). Van aparte de las de
  // arriba, que son de quien entrega: en un rechazo esa es toda la diferencia.
  approvalEvidenceUrl1?: string | null;
  approvalEvidenceUrl2?: string | null;
  approvalEvidenceUrl3?: string | null;
  /**
   * El sello de la aceptación (A-02). Solo lo traen las asignaciones y
   * reasignaciones; el resto de eventos lo trae en nulo.
   */
  approvalStatus?: 'pending' | 'approved' | 'rejected' | null;
  approvalResolvedAt?: string | null;
  approvalDeviceTimestamp?: string | null;
  approvalIp?: string | null;
  termsVersion?: string | null;
  termsVersionTitle?: string | null;
  signatureUrl?: string | null;
  rejectionReason?: string | null;
  /** El rechazo que provocó este retorno, si lo es (A-08). */
  rejectedMovementId?: string | null;
}

// Forma del nuevo endpoint GET /almacen/historial/herramienta/:id
interface HistorialEventDto {
  id: string;
  // assignment | reassignment | return | maintenance_entry | maintenance_return |
  // warehouse_transfer | reactivation | decommission | incident_open | incident_closed
  eventType: string;
  eventDate: string;           // fecha real del evento
  responsibleUserName: string | null;
  performedByUserName: string | null;
  projectName: string | null;
  resultingStatus: string | null;
  notes: string | null;
  decommissionReason?: string | null;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
  approvalEvidenceUrl1?: string | null;
  approvalEvidenceUrl2?: string | null;
  approvalEvidenceUrl3?: string | null;
  /**
   * El sello de la aceptación (A-02). Solo lo traen las asignaciones y
   * reasignaciones; el resto de eventos lo trae en nulo.
   */
  approvalStatus?: 'pending' | 'approved' | 'rejected' | null;
  approvalResolvedAt?: string | null;
  approvalDeviceTimestamp?: string | null;
  approvalIp?: string | null;
  termsVersion?: string | null;
  termsVersionTitle?: string | null;
  signatureUrl?: string | null;
  rejectionReason?: string | null;
  /** El rechazo que provocó este retorno, si lo es (A-08). */
  rejectedMovementId?: string | null;
  createdAt: string;
}

interface HistorialApiResponse {
  data: HistorialEventDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MOVEMENT_ACCION: Record<string, AccionTipo> = {
  assignment:         "Asignación",
  reassignment:       "Reasignación",
  return:             "Devolución",
  maintenance_entry:  "Mantenimiento",
  maintenance_return: "Mantenimiento",
  warehouse_transfer: "Traspaso",
  reactivation:       "Reactivación",
  decommission:       "Baja",
  // La apertura y el cierre de una incidencia son dos eventos distintos en la
  // cronología, pero la misma acción para quien la lee.
  incident_open:      "Incidencia",
  incident_closed:    "Incidencia",
};

const ACCIONES_ORDEN: AccionTipo[] = [
  "Asignación",
  "Reasignación",
  "Devolución",
  "Mantenimiento",
  "Traspaso",
  "Reactivación",
  "Incidencia",
  RETORNO_POR_RECHAZO,
  "Baja",
  "Otro",
];

const DECOMMISSION_REASON_LABEL: Record<string, string> = {
  theft:             "Robo",
  loss:              "Extravío",
  irreparable_damage:"Daño irreparable",
  end_of_life:       "Fin de vida útil",
};

function computeActividad(updatedAt: string): string {
  const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86_400_000);
  if (days <= 3)  return "Reciente";
  if (days < 14)  return `${days} días`;
  const weeks = Math.floor(days / 7);
  if (weeks < 12) return `${weeks} semana${weeks === 1 ? "" : "s"}`;
  return "Inactivo";
}

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-MX", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      timeZone: ZONA_DE_OPERACION,
    });
  } catch { return iso; }
}

const PAGE_SIZE = 12;

// ─── Sort helpers ─────────────────────────────────────────────────────────────

function actividadValue(actividad: string): number {
  if (actividad === "Reciente") return 0;
  const diasMatch = actividad.match(/^(\d+)\s*día/);
  if (diasMatch) return parseInt(diasMatch[1]);
  const semMatch = actividad.match(/^(\d+)\s*semana/);
  if (semMatch) return 7 * parseInt(semMatch[1]) + 50;
  if (actividad.includes("semana")) return 100;
  if (actividad === "Inactivo") return 9999;
  return 500;
}

// ─── Actividad badge ──────────────────────────────────────────────────────────

const getActividadColor = (actividad: string): ColorDeBadge => {
  if (actividad === "Reciente") return "verde";
  if (actividad === "Inactivo") return "gris";
  const diasMatch = actividad.match(/^(\d+)\s*día/);
  if (diasMatch) {
    const n = parseInt(diasMatch[1]);
    if (n <= 7) return "verde";
    if (n <= 14) return "amarillo";
    return "naranja";
  }
  if (actividad.includes("semana")) return "naranja";
  return "gris";
};

const ActividadBadge = ({ value }: { value: string }) => (
  <Badge color={getActividadColor(value)}>{value}</Badge>
);

// ─── Acción badge (detalle) ───────────────────────────────────────────────────

const ACCION_COLOR: Record<AccionTipo, ColorDeBadge> = {
  Asignación: "azul",
  Reasignación: "naranja",
  Mantenimiento: "amarillo",
  Devolución: "verde",
  Incidencia: "rojo",
  // Las dos avisan de algo que salió mal: mismo rojo, la etiqueta las distingue.
  [RETORNO_POR_RECHAZO]: "rojo",
  Traspaso: "morado",
  Reactivación: "verde",
  Baja: "gris",
  Otro: "gris",
};

/**
 * Marca una herramienta que ya no está en circulación (A-09).
 *
 * Se consulta su historial igual que el de cualquiera —es la que más hay que
 * poder consultar—, pero sin la marca se confunde con una que sigue en uso.
 */
const PastillaDeBaja = () => <Badge color="gris">Dada de baja</Badge>;

const AccionBadge = ({ value }: { value: AccionTipo }) => (
  <Badge color={ACCION_COLOR[value]}>{value}</Badge>
);

// ─── Tool image ───────────────────────────────────────────────────────────────

const ToolImage = ({ src, nombre }: { src: string | null; nombre: string }) => (
  <div
    className="flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center bg-gray-50"
    style={{ width: 128, height: 108, border: "1px solid #E0E0E0" }}
  >
    {src ? (
      <img src={src} alt={nombre} className="w-full h-full object-contain" />
    ) : (
      <Wrench className="w-10 h-10 text-gray-300" />
    )}
  </div>
);

// ─── Sort button ──────────────────────────────────────────────────────────────

const SortBtn = ({ label, dir, onClick }: { label: string; dir: SortDir; onClick: () => void }) => {
  const active = dir !== null;
  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-sm text-gray-700 font-semibold">{label}</span>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
          active
            ? "border-[#477FFA] text-[#477FFA] bg-[#EEF4FF]"
            : "border-gray-200 text-gray-500 bg-white hover:bg-gray-50"
        }`}
      >
        <Icon className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Filter dropdown (detalle) ────────────────────────────────────────────────

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) => {
  const pages = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3)       return [1, 2, 3, "...", totalPages];
    if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  }, [page, totalPages]);

  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm transition-colors";

  return (
    <div className="flex items-center justify-center gap-1 pt-6">
      <button onClick={() => onPage(1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsLeft className="w-4 h-4" /></button>
      <button onClick={() => onPage(page - 1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronLeft className="w-4 h-4" /></button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`e-${i}`} className="w-8 h-8 inline-flex items-center justify-center text-sm text-gray-400">...</span>
        ) : (
          <button key={p} onClick={() => onPage(p as number)}
            className={`${btn} font-medium ${page === p ? "text-white" : "text-gray-600 hover:bg-gray-100"}`}
            style={page === p ? { backgroundColor: "#253579" } : undefined}
          >{p}</button>
        )
      )}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronRight className="w-4 h-4" /></button>
      <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsRight className="w-4 h-4" /></button>
    </div>
  );
};

// ─── Movimiento detalle ───────────────────────────────────────────────────────

const LABEL_CLASS = "block text-xs text-gray-400 mb-0.5";
const VALUE_CLASS = "text-sm text-gray-800";

const MovimientoDetalle = ({
  entry,
  herramienta,
  onBack,
}: {
  entry: HistorialEntry;
  herramienta: HerramientaHistorial;
  onBack: () => void;
}) => {
  const noVacias = (urls: (string | null | undefined)[]) =>
    urls.filter((u): u is string => !!u);

  const deQuienEntrega = noVacias([
    entry.evidenceUrl1,
    entry.evidenceUrl2,
    entry.evidenceUrl3,
  ]);
  // Las que toma quien recibe al aceptar o rechazar (A-01).
  const deQuienRecibe = noVacias([
    entry.approvalEvidenceUrl1,
    entry.approvalEvidenceUrl2,
    entry.approvalEvidenceUrl3,
  ]);
  // El visor navega sobre una sola lista, así que van seguidas en el mismo
  // orden en que se pintan.
  const evidencias = [...deQuienEntrega, ...deQuienRecibe];
  const toolPhotos = herramienta.photoUrl ? [herramienta.photoUrl] : [];
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  return (
  <div className="mx-auto px-8 py-8">
    {/* Visor de fotos ampliado */}
    <PhotoViewerModal
      open={viewerIndex !== null}
      onClose={() => setViewerIndex(null)}
      toolPhotos={toolPhotos}
      evidencePhotos={evidencias}
      initialTab="evidencia"
      initialIndex={viewerIndex ?? 0}
    />
    {/* Header */}
    <div className="flex items-start gap-3 mb-6">
      <button
        type="button"
        onClick={onBack}
        className={estiloDeBoton('secundario', 'mt-1.5')}
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{herramienta.nombre}</h1>
        <p className="text-sm text-gray-500 mt-0.5">Detalle de movimiento</p>
      </div>
    </div>

    {/* Información general */}
    <div className="bg-white rounded-xl border shadow-sm mb-5" style={{ borderColor: "#E0E0E0" }}>
      <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
        <h2 className="text-base font-semibold text-gray-800">Información general</h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-4 gap-5">
          <div>
            <p className={LABEL_CLASS}>Acción</p>
            <p className={VALUE_CLASS}>{entry.accion}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Fecha y hora</p>
            <p className={VALUE_CLASS}>{entry.fechaHora}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Responsable</p>
            <p className={VALUE_CLASS}>{entry.responsable}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Realizado por</p>
            <p className={VALUE_CLASS}>{entry.realizadoPor}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Proyecto</p>
            <p className={VALUE_CLASS}>{entry.proyectoAsignado}</p>
          </div>
          {entry.accion === "Baja" && entry.decommissionReason && (
            <div>
              <p className={LABEL_CLASS}>Motivo de baja</p>
              <p className={VALUE_CLASS}>
                {DECOMMISSION_REASON_LABEL[entry.decommissionReason] ??
                  entry.decommissionReason}
              </p>
            </div>
          )}
          <div>
            <p className={LABEL_CLASS}>Fecha de creación</p>
            <p className={VALUE_CLASS}>{herramienta.createdAt ? formatFecha(herramienta.createdAt) : "Sin fecha"}</p>
          </div>
        </div>
        {entry.notes && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: "#E0E0E0" }}>
            <p className={LABEL_CLASS}>Notas</p>
            <p className="text-sm text-gray-700 leading-relaxed">{entry.notes}</p>
          </div>
        )}
      </div>
    </div>

    {/*
      El sello de la aceptación (A-02). No es un movimiento aparte —al aceptar,
      la herramienta no cambia de manos— así que va dentro del detalle de la
      asignación que sella. Se pinta solo si la hay.
    */}
    <SelloDeAceptacion
      sello={entry}
      receptor={entry.responsable === "Sin responsable" ? null : entry.responsable}
      className="mb-5"
    />

    {/* Evidencia fotográfica */}
    <div className="bg-white rounded-xl border shadow-sm" style={{ borderColor: "#E0E0E0" }}>
      <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
        <h2 className="text-base font-semibold text-gray-800">Evidencia fotográfica</h2>
      </div>
      <div className="p-6 space-y-6">
        {/*
          Separadas por quién las tomó. Juntas se perdía lo único que importa
          en un rechazo: unas muestran cómo salió la herramienta y otras cómo
          llegó, y sin la etiqueta no hay forma de saber cuál es cuál.
        */}
        <GrupoDeEvidencia
          titulo="Fotos de quien entrega"
          urls={deQuienEntrega}
          conHuecos
          onVer={(url) => setViewerIndex(evidencias.indexOf(url))}
        />

        {deQuienRecibe.length > 0 && (
          <GrupoDeEvidencia
            titulo="Fotos de quien recibe"
            urls={deQuienRecibe}
            onVer={(url) => setViewerIndex(evidencias.indexOf(url))}
          />
        )}
      </div>
    </div>
  </div>
  );
};

// ─── Historial detalle (pantalla de herramienta individual) ───────────────────

const HistorialDetalle = ({
  herramienta,
  onBack,
}: {
  herramienta: HerramientaHistorial;
  onBack: () => void;
}) => {
  const [search,             setSearch]             = useState("");
  const [filterProyecto,     setFilterProyecto]     = useState("");
  const [filterResponsable,  setFilterResponsable]  = useState("");
  const [filterAccion,       setFilterAccion]       = useState("");
  const [selectedMovimiento, setSelectedMovimiento] = useState<HistorialEntry | null>(null);
  const [movimientos,        setMovimientos]        = useState<HistorialEntry[]>([]);
  const [loadingMov,         setLoadingMov]         = useState(true);

  useEffect(() => {
    setLoadingMov(true);
    apiService
      .request<HistorialApiResponse>(
        `/almacen/historial/herramienta/${herramienta.id}?page=1&limit=100`,
      )
      .then((res) => {
        const mapped: HistorialEntry[] = (res.data ?? []).map((m) => ({
          id:               m.id,
          accion:           accionDelMovimiento(
            MOVEMENT_ACCION[m.eventType] ?? "Otro",
            m.rejectedMovementId,
          ),
          fechaHora:        formatFecha(m.eventDate ?? m.createdAt),
          responsable:      m.responsibleUserName ?? "Sin responsable",
          realizadoPor:     m.performedByUserName ?? "Sin registro",
          proyectoAsignado: m.projectName ?? "Sin proyecto",
          notes:            m.notes,
          decommissionReason: m.decommissionReason,
          evidenceUrl1:     m.evidenceUrl1,
          evidenceUrl2:     m.evidenceUrl2,
          evidenceUrl3:     m.evidenceUrl3,
          approvalEvidenceUrl1: m.approvalEvidenceUrl1,
          approvalEvidenceUrl2: m.approvalEvidenceUrl2,
          approvalEvidenceUrl3: m.approvalEvidenceUrl3,
          approvalStatus:       m.approvalStatus,
          approvalResolvedAt:   m.approvalResolvedAt,
          approvalDeviceTimestamp: m.approvalDeviceTimestamp,
          approvalIp:           m.approvalIp,
          termsVersion:         m.termsVersion,
          termsVersionTitle:    m.termsVersionTitle,
          signatureUrl:         m.signatureUrl,
          rejectionReason:      m.rejectionReason,
          rejectedMovementId:   m.rejectedMovementId,
        }));
        setMovimientos(mapped);
      })
      .catch(() => setMovimientos([]))
      .finally(() => setLoadingMov(false));
  }, [herramienta.id]);

  // El filtro solo ofrece las acciones que la herramienta realmente tiene: el
  // listado fijo mostraba opciones que nunca devolvían filas.
  const ACCIONES = ACCIONES_ORDEN.filter((a) => movimientos.some((r) => r.accion === a));
  const proyectos    = [...new Set(movimientos.map((r) => r.proyectoAsignado).filter((v) => v !== "Sin proyecto"))];
  const responsables = [...new Set(movimientos.map((r) => r.responsable).filter((v) => v !== "Sin responsable"))];

  const rows = movimientos.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.responsable.toLowerCase().includes(q) || r.proyectoAsignado.toLowerCase().includes(q);
    return (
      matchSearch &&
      (!filterProyecto    || r.proyectoAsignado === filterProyecto) &&
      (!filterResponsable || r.responsable      === filterResponsable) &&
      (!filterAccion      || r.accion           === filterAccion)
    );
  });

  // Early return AFTER all hooks
  if (selectedMovimiento) {
    return (
      <MovimientoDetalle
        entry={selectedMovimiento}
        herramienta={herramienta}
        onBack={() => setSelectedMovimiento(null)}
      />
    );
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mt-1.5')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{herramienta.nombre}</h1>
              {herramienta.dadaDeBaja && <PastillaDeBaja />}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Asignaciones, devoluciones e incidencias registradas para esta herramienta
            </p>
          </div>
        </div>
        <button
          type="button"
          className={estiloDeBoton('primario')}
        >
          <Download className="w-4 h-4" />
          Descargar
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Filtrar movimientos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect label="Proyecto asignado" options={(proyectos).map((o) => ({ value: o, label: o }))}    value={filterProyecto}    onChange={setFilterProyecto}    ancho="contenido"
          />
        <FilterSelect label="Responsable"        options={(responsables).map((o) => ({ value: o, label: o }))} value={filterResponsable}  onChange={setFilterResponsable}  ancho="contenido"
          />
        <FilterSelect label="Acción"             options={(ACCIONES).map((o) => ({ value: o, label: o }))}    value={filterAccion}      onChange={setFilterAccion}      ancho="contenido"
          />
        <button
          onClick={() => { setSearch(""); setFilterProyecto(""); setFilterResponsable(""); setFilterAccion(""); }}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                {["Acción", "Fecha y hora", "Responsable", "Realizado por", "Proyecto asignado"].map((h) => (
                  <th
                    key={h}
                    className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 whitespace-nowrap"
                    style={{ color: "#534D59" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingMov ? (
                // A-01: silueta dentro de la tabla, para no perder el ancho de
                // las columnas mientras cargan los movimientos.
                <tr>
                  <td colSpan={5} className="p-0">
                    <EsqueletoTabla filas={6} columnas={5} />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                      <p className="text-sm font-medium text-gray-500">Sin movimientos registrados</p>
                      <p className="text-sm text-gray-400">Esta herramienta aún no tiene historial.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedMovimiento(row)}
                    className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                    style={{ borderBottom: "1px solid #E4E4E4" }}
                  >
                    <td className="py-3 px-4 pl-6 whitespace-nowrap">
                      <AccionBadge value={row.accion} />
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{row.fechaHora}</td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{row.responsable}</td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{row.realizadoPor}</td>
                    <td className="py-3 px-4 pr-6 text-gray-600 whitespace-nowrap">{row.proyectoAsignado}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const HistorialHerramientas = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toolId } = useParams();

  // La herramienta viaja en el state solo para pintar el encabezado sin esperar
  // a que cargue el listado; la que manda es la de la URL.
  const herramientaDelState =
    (location.state as { preselectTool?: HerramientaHistorial } | null)?.preselectTool ?? null;

  // En la URL y no en useState: al volver del detalle la pantalla se monta de
  // nuevo, y con estado en memoria se perdia lo filtrado y la pagina.
  const [search,       setSearch]       = useEstadoEnUrl<string>("buscar", "");
  const [azSort,       setAzSort]       = useEstadoEnUrl<SortDir>("az", "asc");
  const [actSort,      setActSort]      = useEstadoEnUrl<SortDir>("actividad", null);
  const [page,         setPage]         = useEstadoEnUrl<number>("pagina", 1);
  const [tools,        setTools]        = useState<HerramientaHistorial[]>([]);
  const [loading,      setLoading]      = useState(true);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      // Dos consultas porque el listado excluye las dadas de baja salvo que se
      // pidan por estado. Aquí sí interesan: de una herramienta de baja es de
      // la que más hay que consultar, y hasta ahora no se llegaba a ella desde
      // ningún lado (A-09).
      const [activas, bajas] = await Promise.all([
        herramientasApi.findAll({ limit: 100 }),
        herramientasApi.findAll({ limit: 100, status: "decommissioned" }),
      ]);

      const filas = (
        res: Awaited<ReturnType<typeof herramientasApi.findAll>>,
      ) =>
        // El backend puede devolver { data: [...] } o directamente un array
        Array.isArray(res)
          ? (res as unknown as import('@/features/almacen/api/herramientas.api').ToolResponseDto[])
          : (res.data ?? []);

      const mapear = (
        t: import('@/features/almacen/api/herramientas.api').ToolResponseDto,
      ): HerramientaHistorial => ({
        id:        t.id,
        nombre:    t.name,
        codigo:    t.serialCode ?? "",
        internalCode: t.internalCode ?? "",
        fecha:     formatFecha(t.updatedAt),
        createdAt: t.createdAt,
        actividad: computeActividad(t.updatedAt),
        photoUrl:  t.photoUrl1 ?? null,
        dadaDeBaja: t.status === "decommissioned",
      });

      setTools([...filas(activas), ...filas(bajas)].map(mapear));
    } catch {
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTools(); }, [fetchTools]);

  const cycleDir = (prev: SortDir): SortDir =>
    prev === null ? "asc" : prev === "asc" ? "desc" : null;

  const handleAzSort  = () => { setAzSort(cycleDir);  setPage(1); };
  const handleActSort = () => { setActSort(cycleDir); setPage(1); };
  const handleSearch  = (v: string) => { setSearch(v); setPage(1); };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let data = tools.filter(
      (h) => !q || h.nombre.toLowerCase().includes(q) || h.codigo.toLowerCase().includes(q),
    );
    if (azSort || actSort) {
      data = [...data].sort((a, b) => {
        if (actSort) {
          const diff = actividadValue(a.actividad) - actividadValue(b.actividad);
          if (diff !== 0) return actSort === "asc" ? diff : -diff;
        }
        if (azSort) {
          const diff = a.nombre.localeCompare(b.nombre, "es");
          if (diff !== 0) return azSort === "asc" ? diff : -diff;
        }
        return 0;
      });
    }
    return data;
  }, [tools, search, azSort, actSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // La herramienta abierta sale de la URL. Si no llego en el state, se busca en
  // el listado ya cargado: asi la pantalla aguanta un refresh o un link pegado.
  const enElListado = toolId
    ? (herramientaDelState?.id === toolId
        ? herramientaDelState
        : tools.find((t) => t.id === toolId) ?? null)
    : null;

  /**
   * Cuando el listado no la trae, se pide por id.
   *
   * Pasaba con las dadas de baja —el listado las excluye— y la pantalla
   * contestaba «No se encontro la herramienta» sobre una que existe y tiene
   * historial (A-09). Tambien cubre a las que quedan fuera de las primeras
   * cien, que caian en lo mismo sin que nadie lo notara.
   */
  const [herramientaSuelta, setHerramientaSuelta] = useState<HerramientaHistorial | null>(null);
  const [buscandoSuelta, setBuscandoSuelta] = useState(false);

  useEffect(() => {
    if (!toolId || loading || enElListado) {
      setHerramientaSuelta(null);
      return;
    }
    let vigente = true;
    setBuscandoSuelta(true);
    herramientasApi
      .findOne(toolId)
      .then((t) => {
        if (!vigente) return;
        setHerramientaSuelta({
          id: t.id,
          nombre: t.name,
          codigo: t.serialCode ?? "",
          internalCode: t.internalCode ?? "",
          fecha: formatFecha(t.updatedAt),
          createdAt: t.createdAt,
          actividad: computeActividad(t.updatedAt),
          photoUrl: t.photoUrl1 ?? null,
          dadaDeBaja: t.status === "decommissioned",
        });
      })
      .catch(() => vigente && setHerramientaSuelta(null))
      .finally(() => vigente && setBuscandoSuelta(false));
    return () => {
      vigente = false;
    };
  }, [toolId, loading, enElListado]);

  const selectedTool = enElListado ?? herramientaSuelta;

  // Volver regresa a donde estaba el usuario, no siempre al listado: si entro
  // desde la vista de la herramienta, ahi tiene que caer. Cuando la pantalla se
  // abrio directo por URL no hay a donde regresar y se va al listado.
  const volverDelDetalle = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/almacen/historial/herramientas");
  };

  // Early return AFTER all hooks — avoids Rules of Hooks violation
  if (toolId) {
    if (!selectedTool) {
      return (
        <div className="mx-auto px-8 py-8">
          <p className="text-sm text-gray-500">
            {loading || buscandoSuelta
              ? "Cargando herramienta…"
              : "No se encontró la herramienta."}
          </p>
        </div>
      );
    }
    return <HistorialDetalle herramienta={selectedTool} onBack={volverDelDetalle} />;
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Herramientas</h1>
        <p className="text-sm text-gray-500 mt-1">
          Consulta todos los movimientos registrados por herramienta
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar herramienta..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <SortBtn label="AZ"                dir={azSort}  onClick={handleAzSort}  />
        <SortBtn label="Actividad reciente" dir={actSort} onClick={handleActSort} />
      </div>

      {/* Grid */}
      {loading ? (
        // A-01: la rejilla de siluetas conserva la forma de las tarjetas.
        <EsqueletoTarjetas cuantas={6} />
      ) : pageItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-2">
          <p className="text-sm font-medium text-gray-500">Sin herramientas con historial</p>
          <p className="text-sm text-gray-400">Aún no hay herramientas con movimientos registrados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageItems.map((h) => (
            <Revelar key={h.id}>
            <div
              onClick={() =>
                navigate(`/almacen/historial/herramientas/${h.id}`, {
                  state: { preselectTool: h },
                })
              }
              className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
              style={{ border: "1px solid #E0E0E0" }}
            >
              <ToolImage src={h.photoUrl} nombre={h.nombre} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{h.nombre}</p>
                {h.internalCode ? (
                  <p className="text-xs mt-0.5 truncate" style={{ color: "#B9BDC3" }}>Cód. interno: {h.internalCode}</p>
                ) : null}
                {h.codigo ? (
                  <p className="text-xs mt-0.5 truncate" style={{ color: "#B9BDC3" }}>N/S: {h.codigo}</p>
                ) : null}
                <p className="text-xs mt-0.5" style={{ color: "#B9BDC3" }}>{h.fecha}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <ActividadBadge value={h.actividad} />
                  {h.dadaDeBaja && <PastillaDeBaja />}
                </div>
              </div>
            </div>
            </Revelar>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination page={safePage} totalPages={totalPages} onPage={setPage} />
      )}
    </div>
  );
};

export default HistorialHerramientas;
